import { useEffect, useState } from "react";
import { http, formatErr } from "@/lib/api";
import { TID } from "@/lib/testIds";
import { Save, CreditCard, Banknote, Wallet, Smartphone } from "lucide-react";
import { toast } from "sonner";

const GATEWAYS = [
  { key: "stripe", label: "Stripe", desc: "Cards, Apple Pay, Google Pay (global)", icon: CreditCard, fields: [["api_key", "Secret key (sk_...)"]] },
  { key: "razorpay", label: "Razorpay", desc: "Cards, UPI, Netbanking, Wallets (India)", icon: Wallet, fields: [["api_key", "Key ID"], ["api_secret", "Key secret"]] },
  { key: "paypal", label: "PayPal", desc: "Cards + PayPal balance (global)", icon: Wallet, fields: [["api_key", "Client ID"], ["api_secret", "Secret"]] },
  { key: "phonepe", label: "PhonePe", desc: "PhonePe UPI checkout (India)", icon: Smartphone, fields: [["merchant_id", "Merchant ID"], ["api_key", "Salt key"]] },
];

export default function AdminSettings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => http.get("/admin/settings").then((r) => setS(r.data));
  useEffect(() => { load(); }, []);

  const set = (path, value) => {
    setS((prev) => {
      const copy = { ...prev };
      if (path.length === 1) copy[path[0]] = value;
      else copy[path[0]] = { ...(prev[path[0]] || {}), [path[1]]: value };
      return copy;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await http.put("/admin/settings", s);
      toast.success("Settings saved");
      load();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setSaving(false);
    }
  };

  if (!s) return <div className="text-white/55">Loading…</div>;

  return (
    <div>
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold">Settings</h1>
          <p className="text-white/45 text-sm mt-1">Store info, currency, shipping, and payment gateways.</p>
        </div>
        <button onClick={save} disabled={saving} className="bls-admin-btn" data-testid={TID.adminSettingsSave}>
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save settings"}
        </button>
      </div>

      <div className="mt-8 grid lg:grid-cols-2 gap-6">
        <div className="bls-admin-card p-6 space-y-4">
          <h2 className="font-display font-bold">Store</h2>
          <Field label="Store name" v={s.store_name} on={(v) => set(["store_name"], v)} t="set-store-name" />
          <Field label="Tagline" v={s.store_tagline} on={(v) => set(["store_tagline"], v)} t="set-store-tagline" />
          <Field label="Support email" v={s.store_email} on={(v) => set(["store_email"], v)} t="set-store-email" />
          <Field label="Support phone" v={s.store_phone} on={(v) => set(["store_phone"], v)} t="set-store-phone" />
        </div>

        <div className="bls-admin-card p-6 space-y-4">
          <h2 className="font-display font-bold">Money</h2>
          <Field label="Currency code" v={s.currency} on={(v) => set(["currency"], v)} t="set-currency" />
          <Field label="Currency symbol" v={s.currency_symbol} on={(v) => set(["currency_symbol"], v)} t="set-currency-sym" />
          <Field label="Flat shipping rate" v={s.shipping_flat_rate} type="number" on={(v) => set(["shipping_flat_rate"], Number(v))} t="set-shipping" />
          <Field label="Free shipping above" v={s.free_shipping_threshold} type="number" on={(v) => set(["free_shipping_threshold"], Number(v))} t="set-free-shipping" />
          <Field label="Tax rate (%)" v={s.tax_rate_percent} type="number" on={(v) => set(["tax_rate_percent"], Number(v))} t="set-tax" />
        </div>
      </div>

      <h2 className="mt-10 font-display text-xl font-bold">Payment gateways</h2>
      <p className="text-white/45 text-sm mt-1">Toggle and add API credentials. COD is always available unless disabled.</p>

      <div className="mt-4 grid lg:grid-cols-2 gap-6">
        <div className="bls-admin-card p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#7f77dd]/15 text-[#7f77dd] flex items-center justify-center">
              <Banknote className="w-4 h-4" />
            </div>
            <div>
              <p className="font-display font-bold">Cash on Delivery</p>
              <p className="text-xs text-white/45">No setup needed</p>
            </div>
          </div>
          <Toggle v={s.cod_enabled} on={(x) => set(["cod_enabled"], x)} t="set-cod" />
        </div>

        {GATEWAYS.map((g) => {
          const cfg = s[g.key] || {};
          return (
            <div key={g.key} className="bls-admin-card p-6">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#7f77dd]/15 text-[#7f77dd] flex items-center justify-center">
                    <g.icon className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="font-display font-bold">{g.label}</p>
                    <p className="text-xs text-white/45">{g.desc}</p>
                  </div>
                </div>
                <Toggle v={cfg.enabled} on={(x) => set([g.key], { ...cfg, enabled: x })} t={`set-${g.key}-toggle`} />
              </div>
              {cfg.enabled && (
                <div className="mt-4 space-y-3">
                  {g.fields.map(([k, lab]) => (
                    <Field
                      key={k}
                      label={lab}
                      v={cfg[k] || ""}
                      on={(v) => set([g.key], { ...cfg, [k]: v })}
                      t={`set-${g.key}-${k}`}
                      placeholder={k.includes("secret") ? "••••••••" : ""}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-4 grid lg:grid-cols-2 gap-6">
        <div className="bls-admin-card p-6 space-y-4">
          <h2 className="font-display font-bold">Shipping (Shiprocket)</h2>
          <p className="text-xs text-white/45">Add credentials to enable auto-push of orders to Shiprocket. We&apos;ll mint a token and sync tracking back.</p>
          <Field label="Shiprocket email" v={s.shiprocket_email || ""} on={(v) => set(["shiprocket_email"], v)} t="set-ship-email" />
          <Field label="Shiprocket password" v={s.shiprocket_password || ""} on={(v) => set(["shiprocket_password"], v)} t="set-ship-pass" />
          <Field label="Shiprocket Company ID" v={s.shiprocket_company_id || ""} on={(v) => set(["shiprocket_company_id"], v)} t="set-ship-company" />
        </div>
      </div>
    </div>
  );
}

function Field({ label, v, on, type = "text", t, placeholder = "" }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-white/50 font-medium">{label}</label>
      <input type={type} value={v ?? ""} placeholder={placeholder} onChange={(e) => on(e.target.value)} className="bls-admin-input mt-2" data-testid={t} />
    </div>
  );
}
function Toggle({ v, on, t }) {
  return (
    <button type="button" onClick={() => on(!v)} data-testid={t} className={`relative w-11 h-6 rounded-full transition-colors ${v ? "bg-[#7f77dd]" : "bg-white/10"}`}>
      <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${v ? "translate-x-5" : ""}`} />
    </button>
  );
}
